import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Field } from "@base-ui/react/field";
import { Select as BaseSelect } from "@base-ui/react/select";
import {
  CollapseIcon,
  ExpandIcon,
  TickIcon,
} from "../../primitives/Icons/Icons";
import css from "./Select.module.css";

const SelectRoot = React.forwardRef(
  (
    {
      bare = false,
      value,
      defaultValue,
      onValueChange,
      multiple = false,
      disabled = false,
      required = false,
      readOnly = false,
      name,
      label,
      description,
      error,
      placeholder,
      open,
      onOpenChange,
      className,
      children,
      items,
      ...rest
    },
    ref,
  ) => {
    const hasError = Boolean(error);

    const selectContent = (
      <BaseSelect.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        multiple={multiple}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        name={name}
        open={open}
        onOpenChange={onOpenChange}
        items={items}
        {...rest}
      >
        <BaseSelect.Trigger ref={ref} className={css.trigger}>
          <BaseSelect.Value placeholder={placeholder} />
          <ExpandIcon className={css.chevron} />
        </BaseSelect.Trigger>
        <BaseSelect.Portal>
          <BaseSelect.Positioner
            className={css.positioner}
            sideOffset={4}
            align="start"
            alignItemWithTrigger={false}
          >
            <BaseSelect.Popup className={css.popup}>
              <BaseSelect.ScrollUpArrow className={css.scrollArrow}>
                <CollapseIcon />
              </BaseSelect.ScrollUpArrow>
              <BaseSelect.List className={css.list}>{children}</BaseSelect.List>
              <BaseSelect.ScrollDownArrow className={css.scrollArrow}>
                <ExpandIcon />
              </BaseSelect.ScrollDownArrow>
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
    );

    if (bare) {
      return selectContent;
    }

    return (
      <Field.Root
        className={clsx(css.root, className)}
        disabled={disabled}
        invalid={hasError}
      >
        {label && <Field.Label className={css.label}>{label}</Field.Label>}
        {selectContent}
        {description && !hasError && (
          <Field.Description className={css.description}>
            {description}
          </Field.Description>
        )}
        {typeof error === "string" && (
          <Field.Error className={css.error} match>
            {error}
          </Field.Error>
        )}
      </Field.Root>
    );
  },
);

SelectRoot.displayName = "SelectRoot";

SelectRoot.propTypes = {
  bare: PropTypes.bool,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  onValueChange: PropTypes.func,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  name: PropTypes.string,
  label: PropTypes.string,
  description: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  placeholder: PropTypes.string,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
  items: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.any, label: PropTypes.string }),
    ),
    PropTypes.objectOf(PropTypes.node),
  ]),
};

const SelectItem = React.forwardRef(
  ({ value, label, disabled = false, className, children, ...rest }, ref) => (
    <BaseSelect.Item
      ref={ref}
      value={value}
      label={label}
      disabled={disabled}
      className={clsx(css.item, className)}
      {...rest}
    >
      <span className={css.itemIndicator}>
        <BaseSelect.ItemIndicator>
          <TickIcon />
        </BaseSelect.ItemIndicator>
      </span>
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
    </BaseSelect.Item>
  ),
);

SelectItem.displayName = "SelectItem";

SelectItem.propTypes = {
  value: PropTypes.any.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const SelectGroup = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseSelect.Group
      ref={ref}
      className={clsx(css.group, className)}
      {...rest}
    >
      {children}
    </BaseSelect.Group>
  ),
);

SelectGroup.displayName = "SelectGroup";

SelectGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const SelectGroupLabel = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <BaseSelect.GroupLabel
      ref={ref}
      className={clsx(css.groupLabel, className)}
      {...rest}
    >
      {children}
    </BaseSelect.GroupLabel>
  ),
);

SelectGroupLabel.displayName = "SelectGroupLabel";

SelectGroupLabel.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const Select = {
  Root: SelectRoot,
  Item: SelectItem,
  Group: SelectGroup,
  GroupLabel: SelectGroupLabel,
};

export default Select;
